import React, { useState, useEffect } from 'react';
import { formatDate } from '../utils/dateUtils';
import { Link } from 'react-router-dom';
import { useScanContext } from '../contexts/ScanContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import TGChibi, { TGChibiVariants } from '../components/TGChibi';
import { LuChevronDown, LuChevronRight } from "react-icons/lu";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  TagIcon
} from '@heroicons/react/24/outline';

export default function WhitelistManagement() {
  const { refreshTrigger, refreshScanData, refreshWhitelistData } = useScanContext();
  const { user } = useAuth();
  const isFreePlan = user?.plan_type === 'free';
  const [domains, setDomains] = useState([]);
  const [whitelistEntries, setWhitelistEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [refreshIndicator, setRefreshIndicator] = useState(false);

  // Accordion State
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  const [formData, setFormData] = useState({
    domain_id: '',
    whitelisted_domain: '',
    whitelist_type: 'domain',
    default_status: 'not_a_threat',
    reason: ''
  });

  const whitelistTypes = [
    { value: 'domain', label: 'Exact Domain', description: 'Match exact domain name' },
    { value: 'subdomain', label: 'Subdomain', description: 'Match all subdomains (*.example.com)' },
    { value: 'pattern', label: 'Pattern', description: 'Wildcard pattern matching' }
  ];

  const statusOptions = [
    { value: 'to_review', label: 'To Review', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' },
    { value: 'confirmed_threat', label: 'Confirmed Threat', color: 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100' },
    { value: 'not_a_threat', label: 'Not a Threat', color: 'bg-green-200 text-green-900 dark:bg-green-800 dark:text-green-100' },
    { value: 'ignore_always', label: 'Ignore Always', color: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
    { value: 'controlled_domain', label: 'Controlled Domain', color: 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100' }
  ];

  useEffect(() => {
    fetchData();
  }, [refreshTrigger]);

  useEffect(() => {
    const handleWindowFocus = () => {
      setRefreshIndicator(true);
      fetchData();
    };

    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(''); // Clear any previous errors
      const [domainsResponse, whitelistResponse] = await Promise.all([
        api.get('/domains'),
        api.get('/tags/whitelist')
      ]);

      setDomains(domainsResponse.data);
      setWhitelistEntries(whitelistResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load whitelist data');
    } finally {
      setLoading(false);
      setRefreshIndicator(false);
    }
  };

  const getGroupedWhitelist = () => {
    const groups = {};
    whitelistEntries.forEach(entry => {
      // Find domain name
      const domain = domains.find(d => d.id === entry.domain_id);
      const domainName = domain ? domain.domain_name : 'Unknown Domain';
      if (!groups[domainName]) {
        groups[domainName] = [];
      }
      groups[domainName].push(entry);
    });
    return groups;
  };

  const toggleGroup = (domainName) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(domainName)) {
        newSet.delete(domainName);
      } else {
        newSet.add(domainName);
      }
      return newSet;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEntry) {
        // For updates, remove domain_id from the payload
        const { domain_id, ...updateData } = formData;
        await api.put(`/tags/whitelist/${editingEntry.id}`, updateData);
      } else {
        // For creation, pass domain_id as query parameter and remove from body
        const { domain_id, ...createData } = formData;
        await api.post(`/tags/whitelist?domain_id=${domain_id}`, createData);
      }

      await fetchData();
      resetForm();

      // Trigger refresh for both scan and whitelist data
      refreshWhitelistData('whitelist-create-update');
      refreshScanData(null, 'whitelist-change');

    } catch (error) {
      console.error('Error saving whitelist entry:', error);
      setError('Failed to save whitelist entry');
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      domain_id: entry.domain_id,
      whitelisted_domain: entry.whitelisted_domain,
      whitelist_type: entry.whitelist_type,
      default_status: entry.default_status,
      reason: entry.reason || ''
    });
    setShowAddForm(true);
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this whitelist entry?')) {
      return;
    }

    try {
      await api.delete(`/tags/whitelist/${entryId}`);
      await fetchData();

      // Trigger refresh for both scan and whitelist data
      refreshWhitelistData('whitelist-delete');
      refreshScanData(null, 'whitelist-change');

    } catch (error) {
      console.error('Error deleting whitelist entry:', error);
      setError('Failed to delete whitelist entry');
    }
  };

  const resetForm = () => {
    setFormData({
      domain_id: '',
      whitelisted_domain: '',
      whitelist_type: 'domain',
      default_status: 'not_a_threat',
      reason: ''
    });
    setShowAddForm(false);
    setEditingEntry(null);
  };

  const getStatusDisplay = (status) => {
    return statusOptions.find(s => s.value === status) || statusOptions[0];
  };

  const getTypeDisplay = (type) => {
    return whitelistTypes.find(t => t.value === type) || whitelistTypes[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-900 flex items-center justify-center">
        <TGChibiVariants.Loading message="Loading whitelist..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative">
        {isFreePlan && (
          <div className="absolute inset-0 z-10 bg-white/60 dark:bg-dark-900/60 backdrop-blur-[2px] flex items-center justify-center rounded-lg mt-24">
            <div className="bg-white dark:bg-dark-800 p-8 rounded-xl shadow-2xl max-w-md text-center border border-gray-200 dark:border-gray-700">
              <ShieldCheckIcon className="h-16 w-16 text-mint-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Premium Feature</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Upgrade to Premium to use Whitelisting. Automatically tag trusted domains and reduce false positives.
              </p>
              <Link
                to="/settings"
                className="w-full inline-flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-mint-600 hover:bg-mint-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mint-500 transition-colors"
              >
                Upgrade to Premium
              </Link>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Whitelist Management
              </h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Manage trusted domains and configure automatic tagging for scan results
              </p>
            </div>
            <button
              onClick={() => setShowAddForm(true)}
              disabled={isFreePlan}
              className={`inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white ${isFreePlan ? 'bg-gray-400 cursor-not-allowed' : 'bg-mint-600 hover:bg-mint-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mint-500'}`}
            >
              <PlusIcon className="-ml-1 mr-1.5 h-4 w-4" />
              New Whitelist
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Form */}
        {showAddForm && (
          <div className="mb-8 bg-white dark:bg-dark-800 shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                {editingEntry ? 'Edit Whitelist Entry' : 'Add New Whitelist Entry'}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Domain
                    </label>
                    <select
                      value={formData.domain_id}
                      onChange={(e) => setFormData({ ...formData, domain_id: e.target.value })}
                      required
                      className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-mint-500 focus:border-mint-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Select a domain</option>
                      {domains.map(domain => (
                        <option key={domain.id} value={domain.id}>
                          {domain.domain_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Whitelisted Domain
                    </label>
                    <input
                      type="text"
                      value={formData.whitelisted_domain}
                      onChange={(e) => setFormData({ ...formData, whitelisted_domain: e.target.value })}
                      required
                      placeholder="example.com or *.example.com"
                      className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-mint-500 focus:border-mint-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Type
                    </label>
                    <select
                      value={formData.whitelist_type}
                      onChange={(e) => setFormData({ ...formData, whitelist_type: e.target.value })}
                      className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-mint-500 focus:border-mint-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      {whitelistTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label} - {type.description}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Default Status
                    </label>
                    <select
                      value={formData.default_status}
                      onChange={(e) => setFormData({ ...formData, default_status: e.target.value })}
                      className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-mint-500 focus:border-mint-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      {statusOptions.map(status => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Reason
                  </label>
                  <textarea
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    rows={3}
                    placeholder="Explain why this domain should be whitelisted..."
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-mint-500 focus:border-mint-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-mint-600 hover:bg-mint-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mint-500"
                  >
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    {editingEntry ? 'Update Entry' : 'Add Entry'}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mint-500"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Whitelist Entries */}
        <div className="bg-white dark:bg-dark-800 shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Whitelist Entries ({whitelistEntries.length})
            </h3>

            {whitelistEntries.length === 0 ? (
              <div className="text-center py-8">
                <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                  No whitelist entries
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Add trusted domains to automatically tag scan results and reduce false positives.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(getGroupedWhitelist()).map(([domainName, entries]) => (
                  <div key={domainName} className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                    <div
                      className="bg-gray-50 dark:bg-gray-700/50 px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      onClick={() => toggleGroup(domainName)}
                    >
                      <div className="flex items-center space-x-3">
                        {expandedGroups.has(domainName) ?
                          <LuChevronDown className="h-5 w-5 text-gray-500" /> :
                          <LuChevronRight className="h-5 w-5 text-gray-500" />
                        }
                        <span className="font-medium text-gray-900 dark:text-white">{domainName}</span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-mint-100 text-mint-800 dark:bg-mint-900 dark:text-mint-200">
                          {entries.length}
                        </span>
                      </div>
                    </div>

                    {expandedGroups.has(domainName) && (
                      <div className="divide-y divide-gray-200 dark:divide-gray-600 border-t border-gray-200 dark:border-gray-600">
                        {entries.map(entry => (
                          <div key={entry.id} className="p-4 bg-white dark:bg-gray-800">
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
                              <div className="flex-1">
                                <div className="flex flex-wrap items-center gap-2 mb-2">
                                  <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                                    {entry.whitelisted_domain}
                                  </h4>
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusDisplay(entry.default_status).color}`}>
                                    {getStatusDisplay(entry.default_status).label}
                                  </span>
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
                                    {getTypeDisplay(entry.whitelist_type).label}
                                  </span>
                                </div>

                                <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                                  <p><strong>Domain:</strong> {domains.find(d => d.id === entry.domain_id)?.domain_name || 'Unknown'}</p>
                                  {entry.reason && (
                                    <p><strong>Reason:</strong> {entry.reason}</p>
                                  )}
                                  <p className="text-xs text-gray-500">
                                    <strong>Created:</strong> {formatDate(entry.created_at)}
                                    {entry.updated_at && (
                                      <span className="ml-4">
                                        <strong>Updated:</strong> {formatDate(entry.updated_at)}
                                      </span>
                                    )}
                                  </p>
                                </div>
                              </div>

                              <div className="flex space-x-2 mt-3 sm:mt-0">
                                <button
                                  onClick={() => handleEdit(entry)}
                                  disabled={isFreePlan}
                                  className={`inline-flex items-center px-2 py-1 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 ${isFreePlan ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mint-500'}`}
                                >
                                  <PencilIcon className="h-3.5 w-3.5 mr-1" />
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDelete(entry.id)}
                                  disabled={isFreePlan}
                                  className={`inline-flex items-center px-2 py-1 border text-xs font-medium rounded-md ${isFreePlan ? 'border-gray-300 text-gray-400 bg-gray-50 cursor-not-allowed' : 'border-red-300 text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'}`}
                                >
                                  <TrashIcon className="h-3.5 w-3.5 mr-1" />
                                  Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {refreshIndicator && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-green-100 dark:bg-green-900/20 border-t border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 text-center">
          <div className="flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
            </svg>
            <span className="text-sm font-medium">Refreshing whitelist data...</span>
          </div>
        </div>
      )}
    </div>
  );
}
